import { ANALYTICS_DEBUG, ANALYTICS_ENABLED, TELEMETRY_ENDPOINT, getDeviceId } from './config';

/**
 * Client telemetry beacon.
 *
 * The browser no longer talks to Mixpanel. It posts an allowlisted set of
 * events to our own API, which forwards them server-side. Three reasons this
 * shape rather than the Mixpanel SDK:
 *
 *  1. The project token never ships in the bundle.
 *  2. Requests go to our own origin, so ad blockers — which block mixpanel.com
 *     outright — don't silently delete the traffic.
 *  3. Everything the server can already observe (auth, payments, investigations,
 *     callbacks) is emitted by the backend, so it can't be forged or dropped.
 *
 * Only events with no server-side counterpart travel this path: SPA page views
 * and browser-only interactions like the PDF export.
 */

export type TelemetryProps = Record<string, string | number | boolean | undefined>;

interface QueuedEvent {
    event: string;
    page?: string;
    path?: string;
    depth?: number;
    planId?: string;
    attribution?: Record<string, string>;
}

/**
 * Property keys that must never leave the browser.
 *
 * The backend sanitises again — this is the near-side guard so nothing sensitive
 * even reaches the network. Kept in step with `TelemetryService` in the backend
 * repo.
 *
 * Two categories: background-check data, and *any attribute of the user*. The
 * only thing identifying a person in Mixpanel is their user id, resolved
 * server-side from the auth token — never sent from here. No role, status,
 * country or verification state rides along on an event.
 */
const BLOCKED_KEY_PATTERNS = [
    // ---- user attributes ----
    'role',
    'gender',
    'company',
    'verified',
    // 'refer' covers referral / referred / referrer in one.
    'refer',
    'exempt',
    'lifetime',
    // ---- personal / background-check data ----
    'email',
    'phone',
    'firstname',
    'lastname',
    'middlename',
    'fullname',
    'address',
    'dob',
    'birth',
    'nin',
    'bvn',
    'passport',
    'token',
    'password',
    'secret',
    'authorization',
    'account',
    'card',
    'cvv',
    'guarantor',
    'medical',
    'diagnosis',
    'result',
    'report',
    'note',
    'comment',
    'reason',
    'file',
    'url',
    'useragent',
    'ipaddress',
];

/**
 * Blocked as an exact match only — names too generic to block by substring.
 * `status` alone describes a person; `gatewayStatus` describes a payment result.
 * `name` is a person; `planName` is a product label.
 */
const BLOCKED_EXACT_KEYS = ['name', 'contact', 'lead', 'status', 'country', 'state', 'city', 'signedupat'];

const isBlockedKey = (key: string): boolean => {
    const normalised = key.toLowerCase();
    if (BLOCKED_EXACT_KEYS.includes(normalised)) return true;
    return BLOCKED_KEY_PATTERNS.some((pattern) => normalised.includes(pattern));
};

export const sanitize = (properties: TelemetryProps = {}): Record<string, string | number | boolean> => {
    const safe: Record<string, string | number | boolean> = {};

    for (const [key, value] of Object.entries(properties)) {
        if (value === undefined || value === null) continue;
        if (isBlockedKey(key)) {
            if (ANALYTICS_DEBUG) console.warn(`[analytics] dropped blocked property "${key}"`);
            continue;
        }
        const type = typeof value;
        if (type === 'string' || type === 'number' || type === 'boolean') {
            safe[key] = value;
        }
    }

    return safe;
};

/**
 * Events are batched and flushed on a short timer.
 *
 * A burst of navigations becomes one request, and no request sits on the
 * critical path of a click.
 */
let queue: QueuedEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

const FLUSH_DELAY_MS = 1500;
const MAX_BATCH = 20;

const send = (events: QueuedEvent[]): void => {
    if (events.length === 0) return;

    const body = JSON.stringify({ events, deviceId: getDeviceId() });

    try {
        // sendBeacon survives the page being closed mid-navigation, which is
        // exactly when the last page view of a session would otherwise be lost.
        if (typeof navigator !== 'undefined' && 'sendBeacon' in navigator) {
            const blob = new Blob([body], { type: 'application/json' });
            if (navigator.sendBeacon(TELEMETRY_ENDPOINT, blob)) return;
        }

        void fetch(TELEMETRY_ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body,
            credentials: 'include',
            keepalive: true,
        }).catch(() => {
            // Telemetry must never surface an error to the user.
        });
    } catch {
        /* ignore */
    }
};

const flush = (): void => {
    if (flushTimer) {
        clearTimeout(flushTimer);
        flushTimer = null;
    }
    const batch = queue;
    queue = [];
    send(batch);
};

const scheduleFlush = (): void => {
    if (flushTimer) return;
    flushTimer = setTimeout(flush, FLUSH_DELAY_MS);
};

/** Registers page-hide flushing. Safe to call more than once. */
let lifecycleBound = false;

export const initAnalytics = (): void => {
    if (!ANALYTICS_ENABLED || lifecycleBound || typeof document === 'undefined') return;
    lifecycleBound = true;

    // `visibilitychange` rather than `unload`: the latter is unreliable on
    // mobile Safari, which is a large share of this audience.
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') flush();
    });
};

/**
 * Queues a client-only event.
 *
 * Server-observable events (payments, investigations, auth) must NOT be sent
 * here — the backend emits those itself and rejects them on this endpoint.
 */
export const track = (event: string, properties: TelemetryProps = {}): void => {
    const safe = sanitize(properties);

    if (!ANALYTICS_ENABLED) {
        if (ANALYTICS_DEBUG) console.info('[analytics] (disabled)', event, safe);
        return;
    }
    if (ANALYTICS_DEBUG) console.info('[analytics] queued', event, safe);

    const { page, path, depth, planId, ...attribution } = safe;

    queue.push({
        event,
        ...(typeof page === 'string' ? { page } : {}),
        ...(typeof path === 'string' ? { path } : {}),
        ...(typeof depth === 'number' ? { depth } : {}),
        ...(typeof planId === 'string' ? { planId } : {}),
        ...(Object.keys(attribution).length > 0
            ? { attribution: Object.fromEntries(Object.entries(attribution).map(([k, v]) => [k, String(v)])) }
            : {}),
    });

    if (queue.length >= MAX_BATCH) flush();
    else scheduleFlush();
};

/**
 * Identity is bound by the backend at login — it knows who authenticated and
 * holds the Mixpanel credentials. Kept as a no-op so callers don't need to
 * branch; clearing local queue state on logout is all that's left to do.
 */
export const resetAnalytics = (): void => {
    queue = [];
    if (flushTimer) {
        clearTimeout(flushTimer);
        flushTimer = null;
    }
};
